"use client";

import React, { useState } from "react";
import { usePathname } from "next/navigation";
import Image from "@/components/Image";
import NewsletterModal from "@/components/NewsletterModal";
import Text from "@/components/Text";
import Icon from "@/components/Icon";
import type { IconProps } from "@/components/Icon";
import FlagStrip from "@/components/FlagStrip";
import Container from "../Container";
import Socials from "../Socials";
import Logo from "../Logo";
import Link from "@/components/Link";
import Button from "@/components/Button";
import classNames from "@/helpers/classNames";
import type { IFooterDocument } from "@/tools/sanity/schema/documents/footerDocument";
import styles from "./styles.module.scss";

interface FooterProps extends IFooterDocument {
  className?: string;
  socials?: {
    _key: string;
    name: IconProps["title"];
    link: string;
  }[];
}

interface FooterColumnProps {
  title: string;
  className?: string;
  children?: React.ReactNode;
}

const Footer = (props: FooterProps) => {
  const { footer, socials, className } = props;
  const { newsletter, sitemap, navItems, contactDetails, partners, disclaimer, acknowledgement } =
    footer || {};
  const {
    title: newsletterTitle,
    buttonText: newsletterButtonText,
    modalTitle,
    modalDescription,
    modalSubmitText,
    successTitle,
    successMessage,
  } = newsletter || {};
  const { email, phone, address } = contactDetails || {};
  const classes = classNames(styles.footer, className);
  const pathname = usePathname();
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  if (pathname?.startsWith("/studio")) {
    return null;
  }

  return (
    <>
      <a id="footer" tabIndex={-1} />
      <footer className={classes} data-theme="dark">
        {/* newsletter */}
        <div className={styles.newsletterContainer}>
          <Container className={styles.newsletter}>
            <Text
              className={styles.newsletterTitle}
              text={newsletterTitle}
              variant="body"
              size="md"
              color="themeBgDefault"
            />
            <Button
              className={styles.newsletterButton}
              variant="content"
              size="none"
              ariaLabel={newsletterButtonText || "Sign up to our mailing list"}
              onClick={() => setShowNewsletterModal(true)}
            >
              <Text
                className={styles.newsletterButtonText}
                text={newsletterButtonText}
                variant="heading"
                size="md"
                color="themeBgDefault"
              />

              <span className={styles.newsletterIconWrapper}>
                <Icon
                  className={styles.newsletterIcon}
                  title="bapArrowRight"
                  color="themeBgDefault"
                />
                <Icon
                  className={styles.newsletterIcon}
                  title="bapArrowRight"
                  color="themeBgDefault"
                />
              </span>
            </Button>
          </Container>
        </div>

        <div className={styles.flagStripContainer}>
          <FlagStrip className={styles.flagStrip} variant="outlineLight" tiled />
        </div>

        <Container className={styles.container}>
          {/* sitemap and nav */}
          <div className={styles.sitemapWrapper}>
            {sitemap?.map((group, index) => (
              <div key={index} className={styles.column}>
                <FooterColumn title={group.groupTitle}>
                  <div className={styles.columnContent}>
                    {group.links?.map((linkItem, linkIndex) => (
                      <div key={linkIndex} className={styles.columnItem}>
                        {linkItem?.title && (
                          <Text
                            className={styles.columnSubtitle}
                            text={linkItem.title}
                            variant="eyebrow"
                            color="themeFgMuted"
                          />
                        )}

                        {linkItem?.links?.[0] && (
                          <ul className={styles.columnItems}>
                            {linkItem.links.map((link, sublinkIndex) => (
                              <li key={sublinkIndex} className={styles.linkGroupLink}>
                                <Link
                                  {...link.link}
                                  className={styles.link}
                                  size="none"
                                  variant="content"
                                >
                                  {link.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </FooterColumn>
              </div>
            ))}
            <div className={styles.navItems}>
              {navItems?.map((navItem, index) => (
                <Link
                  key={index}
                  {...navItem.link}
                  className={classNames(styles.link, styles.navLink)}
                  size="none"
                  variant="content"
                >
                  <Text text={navItem?.label} variant="heading" weight="medium" />
                </Link>
              ))}
            </div>
          </div>

          {/* socials and partners */}
          <div className={styles.wrapper + " " + styles.socialsWrapper}>
            {/*  socials  */}
            <FooterColumn title="Stay Connected" className={styles.socialsContent}>
              <Socials socials={socials} className={styles.socials} />
            </FooterColumn>

            <FooterColumn className={styles.contact} title="Contact Details">
              {email && (
                <div>
                  <Text text="E: " />
                  <Link
                    className={styles.link}
                    href={`mailto:${email}`}
                    size="none"
                    variant="content"
                  >
                    <Text text={`${email}`} />
                  </Link>
                </div>
              )}

              {phone && (
                <div>
                  <Text text="P: " />
                  <Link className={styles.link} href={`tel:${phone}`} size="none" variant="content">
                    <Text text={`${phone}`} />
                  </Link>
                </div>
              )}

              {address && <Text text={address} />}

              {/* Hardcoded rather than a CMS field: the route is fixed and the
                  page is generated, so there is nothing for an editor to
                  choose — and a sitemap link that can be removed by accident
                  is worse than no field at all. Kept in the footer on every
                  page (so crawlers still reach it) but deliberately quiet. */}
              <div>
                <Link
                  href="/sitemap/html/"
                  className={classNames(styles.link, styles.sitemapLink)}
                  size="none"
                  variant="content"
                  ariaLabel="Sitemap"
                >
                  <Text text="Sitemap" size="xs" color="themeFgMuted" />
                </Link>
              </div>
            </FooterColumn>

            <FooterColumn className={styles.partnersContainer} title="Our Partners">
              <div className={styles.partners}>
                {partners?.map((partner, index) => {
                  // `linkProjection` returns no top-level `text` or `title`, so
                  // Link cannot derive a name from the spread below. Something
                  // has to carry it explicitly or the anchor ends up with an
                  // empty aria-label wrapped around an alt="" image.
                  const partnerName = partner.name || partner.image?.altText || "";

                  const partnerImage = (
                    <Image
                      className={styles.partner}
                      {...partner.image}
                      // Decorative when the surrounding Link is named, so the
                      // logo isn't announced twice. Standalone (no link), the
                      // name has to live on the image instead.
                      altText={partner.link ? "" : partnerName}
                    />
                  );

                  return partner?.link ? (
                    <Link
                      key={index}
                      {...partner.link}
                      variant="content"
                      size="none"
                      newWindow
                      ariaLabel={partnerName || undefined}
                    >
                      {partnerImage}
                    </Link>
                  ) : (
                    <React.Fragment key={index}>{partnerImage}</React.Fragment>
                  );
                })}
              </div>
            </FooterColumn>
          </div>

          {/* disclaimer text */}
          <div className={styles.wrapper}>
            <FooterColumn className={styles.disclaimerTitle} title="Our Policy" />

            {disclaimer?.map((block, index) => (
              <FooterColumn key={index} className={styles.disclaimerColumn} title="">
                <Text
                  className={styles.disclaimerText}
                  text={block}
                  color="themeFgMuted"
                  size="xs"
                />
              </FooterColumn>
            ))}
          </div>

          {/*  acknowledgement */}
          <div className={styles.wrapper}>
            <FooterColumn className={styles.acknowledgement} title="Acknowledgement of Country">
              <Text
                className={styles.acknowledgementText}
                text={acknowledgement}
                color="themeFgMuted"
                size="xs"
              />
            </FooterColumn>

            <Logo className={styles.logo} />
          </div>
        </Container>
      </footer>

      <NewsletterModal
        show={showNewsletterModal}
        onClose={setShowNewsletterModal}
        title={modalTitle}
        description={modalDescription}
        submitText={modalSubmitText}
        successTitle={successTitle}
        successMessage={successMessage}
      />
    </>
  );
};

const FooterColumn = (props: FooterColumnProps) => {
  const { title, className, children } = props;

  return (
    <div className={classNames(styles.column, className)}>
      {/* h2, not h4. These are top-level headings within the footer landmark,
          and page content routinely ends at h1 or h2 — an h4 here skipped a
          level on every page. Sizing comes from `.columnTitle`, so the level is
          free to be correct. */}
      {title && (
        <h2 className={styles.columnTitle}>
          <Text text={title} variant="heading" weight="medium" />
        </h2>
      )}

      {children}
    </div>
  );
};

export default Footer;
